import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/view/components/ui/dialog'
import { Button } from '@/view/components/ui/button'
import { useTable } from '@/app/hooks/useTable'
import { selfHireService } from '@/app/services/selfHireService'
import debounce from 'lodash.debounce'
import { twMerge } from 'tailwind-merge'
import { RadioGroup, RadioGroupItem } from '@/view/components/ui/radio-group'
import { Spinner } from '@/view/components/ui/spinner'

interface CreateLinkModalProps {
  visible: boolean
  newLinkName: string
  setNewLinkName: (name: string) => void
  selectedTable: string
  setSelectedTable: (table: string) => void
  onCreateLink: () => void
  isLoading: boolean
  onClose: () => void
}

const CreateLinkModal = ({
  visible,
  newLinkName,
  setNewLinkName,
  selectedTable,
  setSelectedTable,
  onCreateLink,
  isLoading,
  onClose,
}: CreateLinkModalProps) => {
  const { data: tables, isLoading: isLoadingTables, setParams } = useTable()
  const [inputValue, setInputValue] = useState('')
  const [isNameValid, setIsNameValid] = useState<boolean | null>(null)
  const [isCheckingName, setIsCheckingName] = useState(false)

  useEffect(() => {
    setParams({ isActive: 'true' })
  }, [setParams])

  useEffect(() => {
    if (!visible) {
      setInputValue('')
      setIsNameValid(false)
      setIsCheckingName(false)
    }
  }, [visible])

  const checkNameAvailability = useCallback(
    async (name: string) => {
      if (!name.trim()) {
        setIsNameValid(false)
        return
      }

      setIsCheckingName(true)
      try {
        const isAvailable = await selfHireService.checkName(name.trim())
        setIsNameValid(isAvailable)

        if (isAvailable) setNewLinkName(name.trim())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Erro ao verificar nome:', error)
        setIsNameValid(false)
      } finally {
        setIsCheckingName(false)
      }
    },
    [setNewLinkName],
  )

  const debouncedCheckName = useRef(
    debounce(checkNameAvailability, 2000),
  ).current

  const handleNameChange = (text: string) => {
    setInputValue(text)
    setIsNameValid(null)

    if (text.trim()) {
      debouncedCheckName(text)
    } else {
      setIsNameValid(null)
      setNewLinkName('')
    }
  }

  const isButtonDisabled =
    !newLinkName.trim() ||
    !isNameValid ||
    isCheckingName ||
    isLoading ||
    inputValue.trim() === '' ||
    selectedTable === ''

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar link de autocontratação</DialogTitle>
          <DialogDescription>
            Use esse link para divulgar e acompanhar seus resultados
          </DialogDescription>
        </DialogHeader>
        <div className="mb-4">
          <p>Nome:</p>
          <div className="relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Defina um nome para seu link"
              className={twMerge(
                'mb-4 w-full rounded-lg border border-gray01 p-2',
                isNameValid && 'border-success',
              )}
            />
            <Spinner
              size={'small'}
              show={isCheckingName}
              className="absolute right-2.5 top-2.5"
            />
          </div>
          {isNameValid === false && !isCheckingName && (
            <p className="mb-4 text-xs font-medium text-error">
              Nome Inválido ou já em uso
            </p>
          )}
          <div
            className="mb-4"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            <p className="mb-2">Selecione a tabela:</p>
            {isLoadingTables ? (
              <p>Carregando tabelas...</p>
            ) : (
              <RadioGroup
                onValueChange={(v) => setSelectedTable(v)}
                value={selectedTable}
              >
                {tables?.pages.map((page) =>
                  page.tables.map(
                    (table: {
                      _id: string
                      name: string
                      percents: number
                    }) => (
                      <div key={table._id} className="rounded-lg border p-2">
                        <label className="flex items-center gap-3 px-1">
                          <RadioGroupItem value={table._id} />
                          <div>
                            <p className="text-lg font-semibold">
                              {table.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              Comissão: {table.percents}%
                            </p>
                          </div>
                        </label>
                      </div>
                    ),
                  ),
                )}
              </RadioGroup>
            )}
          </div>
          <Button
            onClick={onCreateLink}
            disabled={isButtonDisabled}
            className="mt-4 w-full rounded-full bg-primary text-white"
          >
            {isLoading ? 'Criando link...' : 'Criar link auto contratação'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CreateLinkModal
